import { AudienceId, EmailMarketingProvider } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import {
    GetAudienceQuery,
    TenantCredentialStatus,
    TenantCredentialType,
    useGetEmailProviderListsQuery,
    useGetTenantCredentialsQuery,
    useTriggerAudienceSyncMutation,
} from "generated/graphql";
import React, { useCallback, useState } from "react";
import { useRegionId } from "store/selectors/hooks";
import { EmailProviderListSelectionDialog } from "./email-provider-list-selection-dialog";
import { isInFlight } from "./audience-sync-status";

interface AudienceSyncButtonProps {
    audienceId: AudienceId;
    audience: GetAudienceQuery["getAudience"];
    provider: EmailMarketingProvider;
    providerDisplayName: string;
    onSyncComplete?: () => void;
}

export const AudienceSyncButton: React.FC<AudienceSyncButtonProps> = ({
    audienceId,
    audience,
    provider,
    providerDisplayName,
    onSyncComplete,
}) => {
    const regionId = useRegionId();
    const [showListDialog, setShowListDialog] = useState(false);

    const getTenantCredentialsQuery = useGetTenantCredentialsQuery();
    const triggerSyncMutation = useTriggerAudienceSyncMutation();

    const audienceEmailProvider = provider;

    const credentialType =
        provider === EmailMarketingProvider.Mailchimp
            ? TenantCredentialType.Mailchimp
            : TenantCredentialType.ConstantContact;

    const credential = getTenantCredentialsQuery.data?.getTenantCredentials.find(
        cred => cred.type === credentialType,
    );

    const isConnected = credential?.status === TenantCredentialStatus.Active;

    const getEmailProviderListsQuery = useGetEmailProviderListsQuery(
        { input: { regionId, provider } },
        { enabled: isConnected },
    );

    const hasProviderTag =
        provider === EmailMarketingProvider.Mailchimp
            ? !!audience?.mailchimp?.tag
            : !!audience?.constantContact?.tag;

    const latestSyncResult =
        provider === EmailMarketingProvider.Mailchimp
            ? audience?.mailchimp?.latestSyncResult
            : audience?.constantContact?.latestSyncResult;
    const inFlight = isInFlight(latestSyncResult);
    const buttonLabel = labelForState(latestSyncResult?.status, providerDisplayName);

    const onSyncClick = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();

            const lists = getEmailProviderListsQuery.data?.getEmailProviderLists || [];

            if (lists.length === 0) {
                alert(
                    `No ${providerDisplayName} lists found. Please create a list in ${providerDisplayName} first.`,
                );
                return;
            } else if (lists.length === 1) {
                await triggerSyncMutation.mutateAsync({
                    input: {
                        audienceId: audienceId,
                        listId: lists[0].id,
                        regionId,
                        provider: audienceEmailProvider,
                    },
                });
                onSyncComplete?.();
            } else {
                setShowListDialog(true);
            }
        },
        [
            providerDisplayName,
            audienceId,
            audienceEmailProvider,
            regionId,
            triggerSyncMutation,
            onSyncComplete,
            getEmailProviderListsQuery,
        ],
    );

    const onSelectList = useCallback(
        async (listId: string) => {
            setShowListDialog(false);
            await triggerSyncMutation.mutateAsync({
                input: {
                    audienceId: audienceId,
                    listId: listId,
                    regionId,
                    provider: audienceEmailProvider,
                },
            });
            onSyncComplete?.();
        },
        [
            audienceId,
            audienceEmailProvider,
            regionId,
            triggerSyncMutation,
            onSyncComplete,
        ],
    );

    if (!isConnected && !hasProviderTag) {
        return null;
    }

    return (
        <>
            {isConnected && (
                <div className="space-y-2">
                    <div className="flex items-center space-x-4 mt-8">
                        <PrimaryButton
                            onClick={onSyncClick}
                            disabled={triggerSyncMutation.isLoading || inFlight}>
                            {buttonLabel}
                        </PrimaryButton>
                        {triggerSyncMutation.isLoading || inFlight ? <Spinner /> : null}
                    </div>

                    <p className="text-sm text-gray-500">
                        By syncing, you confirm you have permission to email these
                        contacts in line with {providerDisplayName}&apos;s email
                        guidelines.
                    </p>
                </div>
            )}

            {!isConnected && hasProviderTag && (
                <div className="mt-8">
                    <InlineBanner type="warning">
                        Your {providerDisplayName} account has been disconnected. Please
                        connect it again to sync with {providerDisplayName}.
                    </InlineBanner>
                </div>
            )}

            <EmailProviderListSelectionDialog
                providerDisplayName={providerDisplayName}
                isOpen={showListDialog}
                lists={getEmailProviderListsQuery.data?.getEmailProviderLists || []}
                isLoading={triggerSyncMutation.isLoading}
                onDismiss={() => setShowListDialog(false)}
                onSelectList={onSelectList}
            />
        </>
    );
};

function labelForState(
    status: string | null | undefined,
    providerDisplayName: string,
): string {
    if (status === "Queued") return "Sync queued…";
    if (status === "Running") return "Syncing…";
    return `Sync to ${providerDisplayName}`;
}
